/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Phase 6 Vendor-Port W3 (Wave-0 infra) — useDependencyGraph (§31, §53.3).
 *
 * THE cascading-reset + last-write-wins engine. The React equivalent of the
 * legacy shared infra:
 *   - dependencies_handler.js (341, DependenciesHandler): a dependency GRAPH
 *     {field:{dependencies:[children]}}; on a PARENT change every descendant is
 *     reset+reloaded via the view's loadParams. Loaded layer-by-layer
 *     (topological sort with depths) so siblings load in parallel but a child
 *     never loads before its parent's new list lands. After each reload,
 *     applyParameterNewValues re-validates the current value against the new
 *     option list — keeping it if still valid, else picking from default_values,
 *     else auto-selecting the sole option, else null.
 *   - versioned_data_handler.js (100, VersionedDataRegistry): per-key
 *     data_version/pending_version so a stale slow response can't overwrite a
 *     newer one (startNewVersion before each load; updateData only commits when
 *     the resolving version === the current pending_version).
 *
 * RTKQ alone does NEITHER (it caches per-URL but has no notion of "a parent
 * changed ⇒ reset these children and re-pick their values"). This hook owns
 * BOTH semantics and is reused by fan / EW / PHX.
 *
 * §53.3 asked for "DependenciesHandler as a reducer": the per-key data registry
 * (options + versions) is a useReducer; the cascade is an async effect driven by
 * a parent-change signal. The option-list RESOLUTION is delegated to a
 * caller-supplied `loadParams(field, parents)` — for CMS-doc-resolved fields the
 * caller passes a resolver that calls uvcCms.getFromCmsData with the narrowed
 * id-array (the §31 universal accessor, exactly as legacy mapToCMS did).
 *
 * This module is PURE STATE + ORCHESTRATION (no JSX, no fetch). The selector
 * owns the actual GET/CMS-doc reads inside its loadParams.
 */

import { useCallback, useMemo, useReducer, useRef } from 'react';

/** A declarative dependency graph: field → its direct child fields (+ optional
 * fallback default ids the engine re-picks from when the old value is invalid). */
export interface DependencyGraphSpec {
  [field: string]: {
    /** direct children whose option lists depend on this field. */
    dependencies?: string[];
    /** ordered fallback ids tried (in order) when the old value is invalid. */
    default_values?: Array<number | string>;
  };
}

/** loadParams(field, parents) → the new option-id list (or rows) for `field`,
 * filtered by the current values of `parents`. May be async (a GET / CMS-doc
 * walk). Returns an array of ids (numbers/strings) OR option rows carrying
 * `id` — both are accepted (the engine extracts ids via `id` or the bare value,
 * mirroring legacy getNumber). */
export type LoadParamsFn = (
  field: string,
  parents: string[]
) => Promise<Array<number | string | { id: number | string; [k: string]: unknown }>>;

/** Per-key registry node (VersionedDataRegistry). */
interface RegistryNode {
  data: Array<number | string | { id: number | string }> | null;
  data_version: number | null;
  pending_version: number | null;
}

interface RegistryState {
  [field: string]: RegistryNode;
}

type RegistryAction =
  | { type: 'startVersion'; field: string }
  | { type: 'commit'; field: string; data: RegistryNode['data']; version: number };

function registryReducer(state: RegistryState, action: RegistryAction): RegistryState {
  switch (action.type) {
    case 'startVersion': {
      const node = state[action.field];
      // startNewVersion: bump pending_version (legacy semantics).
      const pending = node
        ? (node.pending_version ?? node.data_version ?? 0) + 1
        : 1;
      return {
        ...state,
        [action.field]: {
          data: node?.data ?? null,
          data_version: node?.data_version ?? null,
          pending_version: pending,
        },
      };
    }
    case 'commit': {
      const node = state[action.field];
      if (!node) {
        // first registration (version may be the initial seed).
        return {
          ...state,
          [action.field]: { data: action.data, data_version: action.version, pending_version: null },
        };
      }
      // last-write-wins: only commit when the resolving version is the latest
      // pending one (a stale slow response with an older version is dropped).
      if (action.version !== node.pending_version) return state;
      return {
        ...state,
        [action.field]: { data: action.data, data_version: action.version, pending_version: null },
      };
    }
    default:
      return state;
  }
}

/** Extract the id from a node entry (legacy getNumber). */
function toId(el: number | string | { id: number | string }): number | string {
  if (el != null && typeof el === 'object') return (el as any).id ?? el;
  return el;
}

// ---------------------------------------------------------------------------
// the graph topology helpers (port of Graph/GraphNode in dependencies_handler)
// ---------------------------------------------------------------------------

interface GraphTopology {
  parentsOf: Record<string, string[]>;
  /** topologically-sorted child layers per field (depth-grouped). */
  childLayers: Record<string, string[][]>;
}

function buildTopology(spec: DependencyGraphSpec): GraphTopology {
  const children: Record<string, string[]> = {};
  const parentsOf: Record<string, string[]> = {};
  const allFields = new Set<string>();
  for (const field of Object.keys(spec)) {
    allFields.add(field);
    children[field] = spec[field].dependencies ?? [];
    for (const c of children[field]) {
      allFields.add(c);
      (parentsOf[c] ??= []).push(field);
    }
  }
  for (const f of allFields) {
    children[f] ??= [];
    parentsOf[f] ??= [];
  }

  // topologicallySortedChildrenWithLayers: BFS depths over the transitive
  // descendant set, grouped by depth (so each layer can load in parallel).
  const childLayers: Record<string, string[][]> = {};
  for (const root of allFields) {
    const depths: Record<string, number> = {};
    const visit = (node: string, depth: number) => {
      for (const child of children[node] ?? []) {
        if (depths[child] == null || depths[child] <= depth) {
          visit(child, depth + 1);
        }
      }
      depths[node] = depth;
    };
    visit(root, 0);
    // keep only the root's actual descendants (depth > 0), grouped by depth.
    const byDepth: Record<number, string[]> = {};
    for (const [field, d] of Object.entries(depths)) {
      if (d === 0) continue;
      (byDepth[d] ??= []).push(field);
    }
    childLayers[root] = Object.keys(byDepth)
      .map(Number)
      .sort((a, b) => a - b)
      .map((d) => byDepth[d]);
  }
  return { parentsOf, childLayers };
}

// ---------------------------------------------------------------------------
// the hook
// ---------------------------------------------------------------------------

export interface DependencyGraphConfig {
  /** the declarative graph. */
  spec: DependencyGraphSpec;
  /** loads a field's option list filtered by its parents (GET / CMS-doc walk). */
  loadParams: LoadParamsFn;
  /** read the current value of a field (the selector's state model). */
  getValue: (field: string) => number | string | null | undefined;
  /** set a field's value (the selector's state setter; the engine calls this
   * when it must re-pick an invalid value — keeps state authoritative). */
  setValue: (field: string, value: number | string | null) => void;
  /** legacy setDefaultsIfNullChecker: when a field's current value is null and
   * a new default is available, may the engine apply it? (legacy returns true
   * for REQUIRED fields). Defaults to true. */
  shouldSetDefaultIfNull?: (field: string) => boolean;
  /** Per-field re-pick policy, consulted BEFORE the built-in
   * `default_values → sole option → null` rule when the current value is no
   * longer in the freshly-loaded list.
   *
   * The built-in rule is the DependenciesHandler one: an out-of-range child
   * goes null unless the new list has exactly one entry. Coil's legacy cascade
   * is not that — `getCollections` re-picks a concrete value per field (the
   * first row for most, `tube_dias[1]` for the tube diameter, the nearest FH
   * <= the previous one, the `casing_mtl_defaults` entry for the casing
   * material), and the legacy TEMPLATE re-picks the rest implicitly: a select
   * whose value matches no `<option>` falls back to its FIRST option, which is
   * the first real row on every select with no leading `- -`.
   *
   * Return a value to use it, `null` to clear explicitly, or `undefined` to
   * defer to the built-in rule. Omitting the callback keeps the built-in rule
   * everywhere — i.e. no behaviour change for fan / EW / PHX. */
  pickFallback?: (
    field: string,
    ids: Array<number | string>,
    current: number | string | null | undefined
  ) => number | string | null | undefined;
  /** W6.5 F3 — has the user made a real parent change yet? When NOT armed (the
   * initial open/seed cascade), a non-null SAVED value is PRESERVED even when
   * the narrowed list omits it (the engine still commits the narrowed option
   * list for the option <select>, but does not clear/re-pick the saved value).
   * On an ARMED parent change the legacy narrowing+re-pick (dropping an
   * out-of-range child) runs unchanged. Defaults to always-armed (legacy). */
  isArmed?: () => boolean;
}

export interface DependencyGraphHandle {
  /** the current option-id list for a field (the narrowed possible ids). null
   * until first load. */
  optionIds: (field: string) => Array<number | string> | null;
  /** notify the engine a PARENT changed → cascade-reload its descendants. Call
   * this from the selector's onChange for any graph field. */
  onParentChange: (field: string) => void;
  /** the per-field versions (for diagnostics / the verifier). */
  registry: RegistryState;
  /** whether any cascade load is currently in flight. */
  isCascading: boolean;
}

export function useDependencyGraph(config: DependencyGraphConfig): DependencyGraphHandle {
  // Only `spec` is read directly (for topology + the shouldSetDefault default);
  // loadParams/getValue/setValue are read off cfgRef so the async cascade sees
  // fresh callbacks without re-binding the orchestration.
  const { spec } = config;
  const shouldSetDefaultIfNull = config.shouldSetDefaultIfNull ?? (() => true);

  const topology = useMemo(() => buildTopology(spec), [spec]);
  const [registry, dispatch] = useReducer(registryReducer, {} as RegistryState);

  // keep live refs so the async cascade reads fresh callbacks/state.
  const cfgRef = useRef(config);
  cfgRef.current = config;
  const topoRef = useRef(topology);
  topoRef.current = topology;

  // a monotonically-increasing pending counter per field (mirror of the
  // VersionedDataRegistry pending_version — kept in a ref because the reducer
  // state is async; the load resolution must compare against the version it
  // STARTED, and the reducer's `commit` enforces last-write-wins).
  const versionRef = useRef<Record<string, number>>({});
  const inFlight = useRef(0);
  const [, forceRender] = useReducer((n: number) => n + 1, 0);

  /** apply a freshly-loaded option list to a field + re-pick its value
   * (applyParameterNewValues). */
  const applyNewValues = useCallback(
    (field: string, ids: Array<number | string>) => {
      const cfg = cfgRef.current;
      const current = cfg.getValue(field);
      const has = (v: unknown) => ids.some((e) => String(e) == String(v));
      if (current != null && has(current)) return; // still valid → keep
      // W6.5 F3: before a real user parent-change (initial open/seed cascade),
      // PRESERVE a non-null saved value even when the narrowed list omits it —
      // the option list is still committed (above), but the saved value is not
      // cleared/re-picked. On an ARMED change the legacy narrowing+drop runs.
      if (current != null && cfg.isArmed && !cfg.isArmed()) return;
      // re-pick: caller policy → default_values in order → sole option → null.
      const picked = cfg.pickFallback?.(field, ids, current);
      const defaults = cfg.spec[field]?.default_values ?? [];
      let next: number | string | null = null;
      if (picked !== undefined) next = picked;
      else {
        const fromDefault = defaults.find((d) => has(d));
        if (fromDefault != null) next = fromDefault;
        else if (ids.length === 1) next = ids[0];
      }
      if (next == String(current) || next === current) return;
      // legacy: only set when the old value was non-null OR the field opts in.
      if (current != null || shouldSetDefaultIfNull(field)) {
        cfg.setValue(field, next);
      }
    },
    [shouldSetDefaultIfNull]
  );

  /** load one field (start a version, fetch, commit iff still latest, re-pick). */
  const loadField = useCallback(
    async (field: string) => {
      const cfg = cfgRef.current;
      const version = (versionRef.current[field] = (versionRef.current[field] ?? 0) + 1);
      dispatch({ type: 'startVersion', field });
      inFlight.current += 1;
      forceRender();
      try {
        const parents = topoRef.current.parentsOf[field] ?? [];
        const raw = await cfg.loadParams(field, parents);
        // last-write-wins: a newer load superseded this one → drop it.
        if (version !== versionRef.current[field]) return;
        const ids = (raw ?? []).map(toId);
        dispatch({ type: 'commit', field, data: ids, version });
        applyNewValues(field, ids);
      } catch {
        // a failed load leaves the prior list intact (legacy swallows).
      } finally {
        inFlight.current -= 1;
        forceRender();
      }
    },
    [applyNewValues]
  );

  /** cascade: reload a changed parent's descendant LAYERS in order (parallel
   * within a layer). */
  const onParentChange = useCallback(
    (field: string) => {
      const layers = topoRef.current.childLayers[field] ?? [];
      void (async () => {
        for (const layer of layers) {
          await Promise.all(layer.map((child) => loadField(child)));
        }
      })();
    },
    [loadField]
  );

  const optionIds = useCallback(
    (field: string): Array<number | string> | null => {
      const node = registry[field];
      if (!node || node.data == null) return null;
      return node.data.map(toId);
    },
    [registry]
  );

  return {
    optionIds,
    onParentChange,
    registry,
    isCascading: inFlight.current > 0,
  };
}
